import { useState, useRef, useEffect, useCallback } from "react";

const API_PRIMARY = "https://api.myrelay.ai";
const API_FALLBACK = "https://voicebridge-backend-production-e379.up.railway.app";
let API_URL = process.env.REACT_APP_API_URL || API_PRIMARY;
const WS_URL = API_URL.replace(/^https/, "wss");

const getApiKey = () => {
  try { return new URLSearchParams(window.location.search).get("key") || null; } catch { return null; }
};

const LANGUAGES = [
  { code: "en",    dgCode: "en-US",  name: "English",    flag: "\u{1F1FA}\u{1F1F8}" },
  { code: "es",    dgCode: "es",     name: "Spanish",    flag: "\u{1F1EA}\u{1F1F8}" },
  { code: "fr",    dgCode: "fr",     name: "French",     flag: "\u{1F1EB}\u{1F1F7}" },
  { code: "de",    dgCode: "de",     name: "German",     flag: "\u{1F1E9}\u{1F1EA}" },
  { code: "pt-BR", dgCode: "pt-BR",  name: "Portuguese", flag: "\u{1F1E7}\u{1F1F7}" },
  { code: "it",    dgCode: "it",     name: "Italian",    flag: "\u{1F1EE}\u{1F1F9}" },
  { code: "zh",    dgCode: "zh",     name: "Chinese",    flag: "\u{1F1E8}\u{1F1F3}" },
  { code: "ja",    dgCode: "ja",     name: "Japanese",   flag: "\u{1F1EF}\u{1F1F5}" },
  { code: "ko",    dgCode: "ko",     name: "Korean",     flag: "\u{1F1F0}\u{1F1F7}" },
  { code: "ar",    dgCode: "ar",     name: "Arabic",     flag: "\u{1F1F8}\u{1F1E6}" },
  { code: "hi",    dgCode: "hi",     name: "Hindi",      flag: "\u{1F1EE}\u{1F1F3}" },
  { code: "ru",    dgCode: "ru",     name: "Russian",    flag: "\u{1F1F7}\u{1F1FA}" },
  { code: "nl",    dgCode: "nl",     name: "Dutch",      flag: "\u{1F1F3}\u{1F1F1}" },
  { code: "pl",    dgCode: "pl",     name: "Polish",     flag: "\u{1F1F5}\u{1F1F1}" },
  { code: "tr",    dgCode: "tr",     name: "Turkish",    flag: "\u{1F1F9}\u{1F1F7}" },
  { code: "vi",    dgCode: "vi",     name: "Vietnamese", flag: "\u{1F1FB}\u{1F1F3}" },
  { code: "uk",    dgCode: "uk",     name: "Ukrainian",  flag: "\u{1F1FA}\u{1F1E6}" },
  { code: "tl",    dgCode: "tl",     name: "Filipino",   flag: "\u{1F1F5}\u{1F1ED}" },
];

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
  .opi-root { font-family: 'Inter', system-ui, -apple-system, sans-serif; }
  .opi-root * { box-sizing: border-box; margin: 0; padding: 0; border: none; background: none; cursor: default; }
  .opi-root button { cursor: pointer; -webkit-tap-highlight-color: transparent; }
  @keyframes opiFadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
  @keyframes opiPulse { 0%,100% { transform:scale(1); opacity:0.6; } 50% { transform:scale(1.6); opacity:0; } }
  @keyframes opiWave1 { 0%,100%{height:10px} 50%{height:22px} }
  @keyframes opiWave2 { 0%,100%{height:18px} 50%{height:8px} }
  @keyframes opiWave3 { 0%,100%{height:14px} 50%{height:26px} }
  @keyframes opiWave4 { 0%,100%{height:20px} 50%{height:12px} }
  @keyframes opiWave5 { 0%,100%{height:10px} 50%{height:24px} }
  @keyframes opiRingPulse { 0% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); } 70% { box-shadow: 0 0 0 20px rgba(99,102,241,0); } 100% { box-shadow: 0 0 0 0 rgba(99,102,241,0); } }
  .opi-live-ring { animation: opiRingPulse 2s infinite; }
  .opi-lang-btn:active { transform: scale(0.96); }
`;

// Convert Float32 PCM from AudioWorklet → Int16 PCM for Deepgram
const floatTo16BitPCM = (float32) => {
  const out = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i++) {
    const s = Math.max(-1, Math.min(1, float32[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
  }
  return out;
};

export default function OPIWidget({ embedded = false, onClose, initialLangA, initialLangB }) {
  const [langA, setLangA] = useState(() => {
    if (initialLangA) return LANGUAGES.find(l => l.code === initialLangA.code) || LANGUAGES[0];
    return LANGUAGES[0];
  });
  const [langB, setLangB] = useState(() => {
    if (initialLangB) return LANGUAGES.find(l => l.code === initialLangB.code) || LANGUAGES[1];
    return LANGUAGES[1];
  });
  const [transcript, setTranscript] = useState([]);
  const [liveText, setLiveText] = useState("");        // current partial transcript
  const [isLive, setIsLive] = useState(false);          // WS connected + mic active
  const [isSpeaking, setIsSpeaking] = useState(false);  // user currently speaking
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState(null);
  const [picking, setPicking] = useState(null);
  const [started, setStarted] = useState(false);
  const autoStartRef = useRef(false);

  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const micStreamRef = useRef(null);
  const workletNodeRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const currentSpeakerLangRef = useRef(null);  // which language the speaker used
  const isMutedRef = useRef(false);             // mute mic while TTS plays
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [transcript, liveText]);

  // Play TTS audio
  const playAudio = useCallback((b64, mime = "audio/mpeg") => {
    return new Promise((resolve) => {
      try {
        const bin = atob(b64);
        const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        if (audioPlayerRef.current) audioPlayerRef.current.pause();
        const audio = new Audio(URL.createObjectURL(new Blob([arr], { type: mime })));
        audioPlayerRef.current = audio;
        audio.onended = () => { isMutedRef.current = false; resolve(); };
        audio.onerror = () => { isMutedRef.current = false; resolve(); };
        isMutedRef.current = true;  // mute mic while playing so we don't pick up our own output
        audio.play().catch(() => { isMutedRef.current = false; resolve(); });
      } catch (e) { isMutedRef.current = false; resolve(); }
    });
  }, []);

  // Translate + speak
  const translateAndSpeak = useCallback(async (text, fromLang, toLang) => {
    setTranslating(true);
    try {
      const apiKey = getApiKey();
      const res = await fetch(`${API_URL}/api/opi/translate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(apiKey ? { "x-opi-key": apiKey } : {}) },
        body: JSON.stringify({ text, fromLang, toLang }),
      });
      if (!res.ok && API_URL === API_PRIMARY) {
        API_URL = API_FALLBACK;
        return translateAndSpeak(text, fromLang, toLang);
      }
      if (!res.ok) throw new Error("Translation failed");
      const data = await res.json();

      const fromL = LANGUAGES.find(l => l.code === fromLang || l.dgCode === fromLang);
      const toL = LANGUAGES.find(l => l.code === toLang || l.dgCode === toLang);

      setTranscript(t => [...t, {
        original: text,
        translated: data.translated,
        fromFlag: fromL?.flag, toFlag: toL?.flag,
        fromName: fromL?.name, toName: toL?.name,
      }]);

      if (data.audioBase64) await playAudio(data.audioBase64, data.audioMimeType);
    } catch (e) {
      setError(e.message);
      setTimeout(() => setError(null), 3000);
    } finally {
      setTranslating(false);
    }
  }, [playAudio]);

  // Stop everything
  const stopSession = useCallback(() => {
    if (wsRef.current) { try { wsRef.current.close(); } catch {} wsRef.current = null; }
    if (workletNodeRef.current) { try { workletNodeRef.current.disconnect(); } catch {} workletNodeRef.current = null; }
    if (micStreamRef.current) { micStreamRef.current.getTracks().forEach(t => t.stop()); micStreamRef.current = null; }
    if (audioContextRef.current) { try { audioContextRef.current.close(); } catch {} audioContextRef.current = null; }
    setIsLive(false); setIsSpeaking(false); setLiveText("");
  }, []);

  // Start continuous session
  const startSession = useCallback(async () => {
    setError(null);
    try {
      // 1. Get mic access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      micStreamRef.current = stream;

      // 2. Connect WebSocket
      const apiKey = getApiKey();
      const wsUrl = `${WS_URL}/opi-stream?lang=multi${apiKey ? `&key=${apiKey}` : ""}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      let currentUtterance = "";
      let utteranceLang = null;

      ws.onopen = () => { setIsLive(true); setStarted(true); };

      ws.onmessage = async (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === "transcript") {
            if (msg.detectedLanguage) utteranceLang = msg.detectedLanguage;
            if (!msg.isFinal) {
              setLiveText(msg.text);
              setIsSpeaking(true);
            } else {
              currentUtterance = (currentUtterance ? currentUtterance + " " : "") + msg.text;
              setLiveText(currentUtterance);
            }
          }

          if (msg.type === "speech_started") {
            setIsSpeaking(true);
          }

          if (msg.type === "utterance_end") {
            setIsSpeaking(false);
            if (currentUtterance.trim()) {
              const spoken = currentUtterance.trim();
              currentUtterance = "";
              setLiveText("");

              // Determine direction: which language did they speak → translate to the other
              const langACode = langA.dgCode.split("-")[0];
              const langBCode = langB.dgCode.split("-")[0];
              const detected = (utteranceLang || "").split("-")[0];

              let fromLang, toLang;
              if (detected && detected === langACode) {
                fromLang = langA.code; toLang = langB.code;
              } else if (detected && detected === langBCode) {
                fromLang = langB.code; toLang = langA.code;
              } else {
                // Fallback: guess based on which speaker went last, default to A→B
                fromLang = currentSpeakerLangRef.current === langA.code ? langB.code : langA.code;
                toLang = currentSpeakerLangRef.current === langA.code ? langA.code : langB.code;
              }
              currentSpeakerLangRef.current = fromLang;
              utteranceLang = null;

              translateAndSpeak(spoken, fromLang, toLang);
            }
          }

          if (msg.type === "error") setError(msg.message);
        } catch (e) { console.error("[OPI] Message parse error:", e); }
      };

      ws.onerror = () => { setError("Connection error. Please try again."); stopSession(); };
      ws.onclose = () => setIsLive(false);

      // 3. Set up audio capture at 16kHz PCM
      const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      audioContextRef.current = ctx;

      // AudioWorklet inline source
      const workletCode = `
        class PCMProcessor extends AudioWorkletProcessor {
          process(inputs) {
            const input = inputs[0];
            if (input && input[0]) {
              this.port.postMessage(input[0]);
            }
            return true;
          }
        }
        registerProcessor('pcm-processor', PCMProcessor);
      `;
      const blob = new Blob([workletCode], { type: "application/javascript" });
      const url = URL.createObjectURL(blob);
      await ctx.audioWorklet.addModule(url);

      const source = ctx.createMediaStreamSource(stream);
      const worklet = new AudioWorkletNode(ctx, "pcm-processor");
      workletNodeRef.current = worklet;

      worklet.port.onmessage = (e) => {
        if (isMutedRef.current) return;
        if (ws.readyState !== WebSocket.OPEN) return;
        const pcm16 = floatTo16BitPCM(e.data);
        ws.send(pcm16.buffer);
      };

      source.connect(worklet);
      // Don't connect worklet to destination — we don't want to hear ourselves
    } catch (e) {
      console.error("[OPI] Start error:", e);
      if (e.name === "NotAllowedError") {
        setError("Microphone access denied. Please allow it in your browser settings.");
      } else {
        setError(e.message || "Failed to start");
      }
      stopSession();
    }
  }, [langA, langB, stopSession, translateAndSpeak]);

  useEffect(() => {
    return () => stopSession();
  }, [stopSession]);

  // Auto-start if launched from modal (both initial languages provided)
  useEffect(() => {
    if (initialLangA && initialLangB && !autoStartRef.current && !started) {
      autoStartRef.current = true;
      // Small delay to let mount complete before requesting mic
      const timer = setTimeout(() => { startSession(); }, 150);
      return () => clearTimeout(timer);
    }
  }, [initialLangA, initialLangB, started, startSession]);

  // ── Language Picker ───
  if (picking) {
    const current = picking === "a" ? langA : langB;
    const other = picking === "a" ? langB : langA;
    return (
      <div className="opi-root" style={{display:"flex",flexDirection:"column",height:"100%",background:"#FAFBFC"}}>
        <style>{css}</style>
        <div style={{padding:"20px 24px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <span style={{fontSize:18,fontWeight:700,color:"#1a1a2e",letterSpacing:"-0.02em"}}>Choose language</span>
          <button onClick={()=>setPicking(null)} style={{fontSize:14,fontWeight:600,color:"#6366F1"}}>Done</button>
        </div>
        <div style={{flex:1,overflowY:"auto",padding:"0 16px 24px",display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,alignContent:"start"}}>
          {LANGUAGES.filter(l => l.code !== other.code).map(lang => {
            const sel = current.code === lang.code;
            return (
              <button key={lang.code} className="opi-lang-btn" onClick={() => {
                if (picking === "a") setLangA(lang); else setLangB(lang);
                setPicking(null);
              }}
                style={{padding:"16px 8px",borderRadius:16,display:"flex",flexDirection:"column",alignItems:"center",gap:8,transition:"all 0.15s",
                  background:sel?"#EEF2FF":"#fff",
                  border:sel?"2px solid #6366F1":"1.5px solid #E5E7EB",
                  boxShadow:sel?"0 0 0 3px rgba(99,102,241,0.1)":"0 1px 3px rgba(0,0,0,0.04)"}}>
                <span style={{fontSize:28,lineHeight:1}}>{lang.flag}</span>
                <span style={{fontSize:12,fontWeight:600,color:sel?"#4338CA":"#4B5563"}}>{lang.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Start Screen ───
  if (!started) {
    return (
      <div className="opi-root" style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"100%",background:"linear-gradient(180deg,#FAFBFC 0%,#F0F0FF 100%)",padding:32,gap:32}}>
        <style>{css}</style>

        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
          <div style={{width:72,height:72,borderRadius:22,background:"linear-gradient(135deg,#6366F1,#8B5CF6)",display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 8px 32px rgba(99,102,241,0.25)"}}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z"/>
              <path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
          </div>
          <div style={{textAlign:"center",marginTop:4}}>
            <div style={{fontSize:24,fontWeight:800,color:"#1a1a2e",letterSpacing:"-0.03em"}}>Interpreter</div>
            <div style={{fontSize:14,color:"#6B7280",fontWeight:500,marginTop:2}}>Speak naturally. We'll handle the rest.</div>
          </div>
        </div>

        <div style={{display:"flex",alignItems:"center",gap:16,width:"100%",maxWidth:340}}>
          <button onClick={()=>setPicking("a")}
            style={{flex:1,padding:"20px 14px",borderRadius:20,background:"#fff",border:"1.5px solid #E5E7EB",display:"flex",flexDirection:"column",alignItems:"center",gap:8,boxShadow:"0 2px 8px rgba(0,0,0,0.04)",transition:"all 0.15s"}}>
            <span style={{fontSize:36,lineHeight:1}}>{langA.flag}</span>
            <span style={{fontSize:14,fontWeight:700,color:"#1a1a2e"}}>{langA.name}</span>
            <span style={{fontSize:11,color:"#9CA3AF",fontWeight:500}}>Tap to change</span>
          </button>

          <button onClick={()=>{setLangA(langB);setLangB(langA);}}
            style={{width:44,height:44,borderRadius:22,background:"#fff",border:"1.5px solid #E5E7EB",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 16l-4-4 4-4"/><path d="M17 8l4 4-4 4"/><path d="M3 12h18"/>
            </svg>
          </button>

          <button onClick={()=>setPicking("b")}
            style={{flex:1,padding:"20px 14px",borderRadius:20,background:"#fff",border:"1.5px solid #E5E7EB",display:"flex",flexDirection:"column",alignItems:"center",gap:8,boxShadow:"0 2px 8px rgba(0,0,0,0.04)",transition:"all 0.15s"}}>
            <span style={{fontSize:36,lineHeight:1}}>{langB.flag}</span>
            <span style={{fontSize:14,fontWeight:700,color:"#1a1a2e"}}>{langB.name}</span>
            <span style={{fontSize:11,color:"#9CA3AF",fontWeight:500}}>Tap to change</span>
          </button>
        </div>

        <button onClick={startSession}
          style={{width:"100%",maxWidth:340,padding:"18px",borderRadius:16,background:"linear-gradient(135deg,#6366F1,#8B5CF6)",color:"#fff",fontSize:17,fontWeight:700,letterSpacing:"-0.01em",boxShadow:"0 4px 20px rgba(99,102,241,0.35)",transition:"all 0.15s"}}>
          Start Session
        </button>

        {error && (
          <div style={{padding:"12px 16px",borderRadius:12,background:"#FEF2F2",border:"1px solid #FECACA",fontSize:13,color:"#DC2626",fontWeight:500,maxWidth:340,textAlign:"center"}}>
            {error}
          </div>
        )}

        <div style={{fontSize:12,color:"#9CA3AF",fontWeight:500,textAlign:"center",lineHeight:1.6}}>
          Powered by Frederick Interpreting
        </div>

        {embedded && onClose && (
          <button onClick={onClose} style={{fontSize:13,color:"#6B7280",fontWeight:600,marginTop:8}}>Close</button>
        )}
      </div>
    );
  }

  // ── Active Session ───
  return (
    <div className="opi-root" style={{display:"flex",flexDirection:"column",height:"100%",background:"#FAFBFC"}}>
      <style>{css}</style>

      {/* Header */}
      <div style={{padding:"14px 20px",background:"#fff",borderBottom:"1px solid #F0F0F5",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <div style={{position:"relative"}}>
            <div className={isLive ? "opi-live-ring" : ""} style={{width:32,height:32,borderRadius:10,background:isLive?"linear-gradient(135deg,#10B981,#059669)":"linear-gradient(135deg,#6366F1,#8B5CF6)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
              </svg>
            </div>
          </div>
          <div>
            <div style={{fontSize:15,fontWeight:700,color:"#1a1a2e",lineHeight:1.2}}>
              {isLive ? (isSpeaking ? "Listening..." : "Live") : "Connecting..."}
            </div>
            <div style={{fontSize:11,color:"#9CA3AF",fontWeight:500}}>{langA.flag} {langA.name} · {langB.flag} {langB.name}</div>
          </div>
        </div>
        <button onClick={()=>{stopSession();setStarted(false);setTranscript([]);}}
          style={{fontSize:13,fontWeight:600,color:"#EF4444",padding:"6px 14px",borderRadius:10,background:"rgba(239,68,68,0.06)",border:"1px solid rgba(239,68,68,0.15)"}}>
          End
        </button>
      </div>

      {error && (
        <div style={{margin:"12px 16px 0",padding:"12px 16px",borderRadius:12,background:"#FEF2F2",border:"1px solid #FECACA",fontSize:13,color:"#DC2626",fontWeight:500,animation:"opiFadeIn 0.3s ease"}}>
          {error}
        </div>
      )}

      {/* Transcript */}
      <div style={{flex:1,overflowY:"auto",padding:"16px 16px 8px",display:"flex",flexDirection:"column",gap:16}}>
        {transcript.length === 0 && !liveText && !translating && (
          <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",flexDirection:"column",gap:16}}>
            <div style={{width:64,height:64,borderRadius:20,background:"#F3F4F6",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#C4C7CE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/>
              </svg>
            </div>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:16,fontWeight:700,color:"#374151",marginBottom:4}}>Start speaking</div>
              <div style={{fontSize:13,color:"#9CA3AF",lineHeight:1.6,maxWidth:260}}>
                Speak in {langA.name} or {langB.name}.<br/>
                The translation will play automatically.
              </div>
            </div>
          </div>
        )}

        {transcript.map((t, i) => (
          <div key={i} style={{display:"flex",flexDirection:"column",gap:8,animation:"opiFadeIn 0.35s ease"}}>
            <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
              <div style={{width:36,height:36,borderRadius:12,background:"#F3F4F6",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:18}}>
                {t.fromFlag}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:11,fontWeight:600,color:"#9CA3AF",marginBottom:4}}>{t.fromName}</div>
                <div style={{padding:"12px 16px",borderRadius:"16px 16px 16px 4px",background:"#fff",border:"1px solid #E5E7EB",fontSize:15,lineHeight:1.6,color:"#374151",boxShadow:"0 1px 3px rgba(0,0,0,0.03)"}}>
                  {t.original}
                </div>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"flex-start",gap:10,flexDirection:"row-reverse"}}>
              <div style={{width:36,height:36,borderRadius:12,background:"#EEF2FF",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:18}}>
                {t.toFlag}
              </div>
              <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"flex-end"}}>
                <div style={{fontSize:11,fontWeight:600,color:"#6366F1",marginBottom:4}}>{t.toName}</div>
                <div style={{padding:"12px 16px",borderRadius:"16px 16px 4px 16px",background:"linear-gradient(135deg,#EEF2FF,#F5F3FF)",border:"1px solid #DDD6FE",fontSize:15,lineHeight:1.6,color:"#4338CA",boxShadow:"0 1px 3px rgba(99,102,241,0.06)"}}>
                  {t.translated}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Live partial transcript */}
        {liveText && (
          <div style={{display:"flex",alignItems:"flex-start",gap:10,animation:"opiFadeIn 0.2s ease"}}>
            <div style={{width:36,height:36,borderRadius:12,background:"#F3F4F6",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:18,opacity:0.6}}>
              🎙
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:11,fontWeight:600,color:"#9CA3AF",marginBottom:4}}>Listening</div>
              <div style={{padding:"12px 16px",borderRadius:"16px 16px 16px 4px",background:"#F9FAFB",border:"1px dashed #D1D5DB",fontSize:15,lineHeight:1.6,color:"#6B7280",fontStyle:"italic"}}>
                {liveText}
              </div>
            </div>
          </div>
        )}

        {translating && (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:12,animation:"opiFadeIn 0.3s ease"}}>
            <div style={{display:"flex",alignItems:"flex-end",gap:3,height:24}}>
              {[1,2,3,4,5].map(n => (
                <div key={n} style={{width:3,borderRadius:2,background:"#6366F1",animation:`opiWave${n} 0.8s ease-in-out infinite`,animationDelay:`${n*0.08}s`}}/>
              ))}
            </div>
            <span style={{fontSize:13,fontWeight:600,color:"#6366F1"}}>Translating...</span>
          </div>
        )}
        <div ref={endRef}/>
      </div>

      {/* Live indicator strip */}
      <div style={{padding:"14px 16px 20px",background:"#fff",borderTop:"1px solid #F0F0F5",flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",gap:10}}>
        <div style={{display:"flex",alignItems:"flex-end",gap:3,height:24,opacity:isSpeaking?1:0.3,transition:"opacity 0.2s"}}>
          {[1,2,3,4,5].map(n => (
            <div key={n} style={{width:4,borderRadius:2,background:isSpeaking?"#10B981":"#D1D5DB",
              animation:isSpeaking?`opiWave${n} 0.7s ease-in-out infinite`:"none",
              animationDelay:`${n*0.08}s`,
              height: isSpeaking ? undefined : "10px"}}/>
          ))}
        </div>
        <span style={{fontSize:13,fontWeight:600,color:isSpeaking?"#10B981":"#9CA3AF",transition:"color 0.2s"}}>
          {isSpeaking ? "Listening..." : isLive ? "Ready — speak anytime" : "Connecting..."}
        </span>
      </div>

      <div style={{fontSize:11,color:"#C4C7CE",textAlign:"center",padding:"0 0 12px",fontWeight:500}}>
        Powered by Frederick Interpreting
      </div>
    </div>
  );
}
